/**
 * Custom hook for synkronisering av aktiviteter
 */

import { useState } from 'react';
import { addActivity, updateLeaderboard, updateUserProfile, getCompetitionsCollection, deleteActivity } from '../services/firestore';
import { getActivities, refreshAccessToken, isTokenExpired } from '../services/strava';
import { getDocs, query, where } from 'firebase/firestore';
import { calculateKmForPeriod } from '../utils/calculations';
import { SYNC_CONFIG } from '../constants/config';

/**
 * Hook for å synkronisere aktiviteter fra Strava
 */
export const useSync = (user, profile, currentTotalKm, existingActivities = []) => {
  const [syncing, setSyncing] = useState(false);

  const syncActivities = async () => {
    if (!user || user.isAnonymous) {
      console.warn('Cannot sync: User not authenticated');
      return;
    }

    console.log('Starting sync...', { 
      stravaConnected: profile?.stravaConnected,
      hasAccessToken: !!profile?.stravaAccessToken,
      expiresAt: profile?.stravaExpiresAt
    });

    // Sjekk om Strava er koblet til
    if (!profile?.stravaConnected) {
      alert('Du må koble til Strava først. Gå til Profil-fanen.');
      return;
    }

    if (!profile?.stravaAccessToken) {
      alert('Ingen Strava access token funnet. Prøv å koble til Strava på nytt.');
      return;
    }

    setSyncing(true);

    try {
      let accessToken = profile.stravaAccessToken;
      
      // Sjekk om token er utløpt og refresh hvis nødvendig
      if (isTokenExpired(profile.stravaExpiresAt)) {
        console.log('Access token expired, refreshing...');
        const tokenData = await refreshAccessToken(profile.stravaRefreshToken);
        
        // Oppdater tokens i Firestore
        await updateUserProfile(user.uid, {
          stravaAccessToken: tokenData.access_token,
          stravaRefreshToken: tokenData.refresh_token,
          stravaExpiresAt: tokenData.expires_at
        });
        
        accessToken = tokenData.access_token;
      }

      console.log('Fetching activities from Strava...');

      // Hent aktiviteter fra Strava (siste X måneder, maks Y aktiviteter)
      const syncDate = new Date();
      syncDate.setMonth(syncDate.getMonth() - SYNC_CONFIG.SYNC_PERIOD_MONTHS);
      
      const activities = await getActivities(accessToken, syncDate, SYNC_CONFIG.MAX_ACTIVITIES_PER_SYNC);
      
      console.log(`Received ${activities.length} activities from Strava`);
      
      // Filtrer kun løpeaktiviteter
      const runActivities = activities.filter(a => a.type === 'Run');
      
      console.log(`Found ${runActivities.length} running activities`);
      
      if (runActivities.length === 0) {
        alert(`Ingen nye løpeaktiviteter funnet de siste ${SYNC_CONFIG.SYNC_PERIOD_MONTHS} månedene`);
        return;
      }

      // Hent eksisterende Strava IDs for å unngå duplikater
      const existingStravaIds = new Set(
        existingActivities
          .filter(a => a.stravaId)
          .map(a => String(a.stravaId))
      );

      console.log(`Found ${existingStravaIds.size} existing activities in database`);

      // Legg til aktiviteter i Firestore (kun nye)
      let totalNewKm = 0;
      let addedCount = 0;
      let skippedCount = 0;
      let invalidCount = 0;
      const newActivitiesData = [];

      for (const activity of runActivities) {
        const stravaId = String(activity.id);
        
        // Sjekk om aktiviteten allerede eksisterer
        if (existingStravaIds.has(stravaId)) {
          console.log(`Skipping duplicate activity: ${activity.name} (ID: ${stravaId})`);
          skippedCount++;
          continue;
        }

        const km = activity.distance / 1000; // Konverter meter til km
        
        // Valider distanse før vi prøver å legge til
        if (km <= 0 || km > 500) {
          console.warn(`Skipping invalid activity: ${activity.name} - ${km.toFixed(2)} km (outside valid range 0-500 km)`);
          invalidCount++;
          continue;
        }
        
        const activityData = {
          km: km,
          duration: activity.moving_time,
          avg_heartrate: activity.average_heartrate || null,
          max_heartrate: activity.max_heartrate || null,
          avg_speed: activity.average_speed || null,
          max_speed: activity.max_speed || null,
          fastest_km_seconds: activity.max_speed ? (1000 / activity.max_speed) : null,
          date: activity.start_date,
          type: 'Run',
          source: 'Strava',
          stravaId: stravaId,
          name: activity.name
        };
        
        console.log(`Adding new activity: ${activity.name} - ${km.toFixed(2)} km`);
        
        try {
          await addActivity(user.uid, activityData);
          newActivitiesData.push(activityData);
          
          totalNewKm += km;
          addedCount++;
        } catch (error) {
          console.error(`Failed to add activity ${activity.name}:`, error);
          invalidCount++;
        }
      }

      console.log(`Added ${addedCount} new activities, skipped ${skippedCount} duplicates, ${invalidCount} invalid activities`);

      // Beregn total km fra alle gjenværende aktiviteter (selv om ingen nye ble lagt til)
      let deletedActivities = [];
      
      // Håndter maks aktiviteter per bruker - slett de eldste hvis vi overskrider
      const totalActivities = existingActivities.length + addedCount;
      
      if (totalActivities > SYNC_CONFIG.MAX_ACTIVITIES_PER_USER) {
        const activitiesToDelete = totalActivities - SYNC_CONFIG.MAX_ACTIVITIES_PER_USER;
        console.log(`Total activities (${totalActivities}) exceeds limit (${SYNC_CONFIG.MAX_ACTIVITIES_PER_USER}). Deleting ${activitiesToDelete} oldest activities...`);
        
        // Sorter eksisterende aktiviteter etter dato (eldste først)
        const sortedActivities = [...existingActivities].sort((a, b) => 
          new Date(a.date) - new Date(b.date)
        );
        
        // Slett de eldste aktivitetene
        for (let i = 0; i < activitiesToDelete; i++) {
          const activityToDelete = sortedActivities[i];
          if (activityToDelete.id) {
            console.log(`Deleting old activity: ${activityToDelete.name || 'Unknown'} from ${activityToDelete.date}`);
            await deleteActivity(user.uid, activityToDelete.id);
            deletedActivities.push(activityToDelete);
          }
        }
      }

      // Beregn total km fra alle gjenværende aktiviteter
      const remainingActivities = existingActivities.filter(
        a => !deletedActivities.find(d => d.id === a.id)
      );
      const allCurrentActivities = [...remainingActivities, ...newActivitiesData];
      const calculatedTotalKm = allCurrentActivities.reduce((sum, activity) => sum + (activity.km || 0), 0);
      
      console.log(`Calculated total km from ${allCurrentActivities.length} activities: ${calculatedTotalKm.toFixed(1)} km`);

      // Hent alle konkurranser brukeren deltar i
      const competitionsQuery = query(
        getCompetitionsCollection(),
        where('participants', 'array-contains', user.uid)
      );
      const competitionsSnapshot = await getDocs(competitionsQuery);
      const userCompetitions = competitionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      console.log(`Found ${userCompetitions.length} competitions user participates in`);

      // Beregn km for hver konkurranse
      const competitionStats = {};
      
      for (const comp of userCompetitions) {
        const compId = comp.id;
        
        // Hopp over arkiverte konkurranser - de skal ikke oppdateres
        if (comp.archived) {
          console.log(`Skipping archived competition: ${comp.title} (${compId})`);
          continue;
        }
        
        // Bruk startDate hvis tilgjengelig, ellers createdAt som fallback
        const startDate = comp.startDate || comp.createdAt;
        
        if (startDate) {
          // For konkurranser uten endDate (first_to_goal, tug_of_war), bruk dagens dato
          const endDate = comp.endDate || new Date().toISOString();
          const kmInPeriod = calculateKmForPeriod(allCurrentActivities, startDate, endDate);
          competitionStats[compId] = kmInPeriod;
          
          console.log(`Competition ${comp.title} (${comp.mode}):`);
          console.log(`  - ID: ${compId}`);
          console.log(`  - Start: ${startDate} ${!comp.startDate ? '(using createdAt)' : ''}`);
          console.log(`  - End: ${endDate}`);
          console.log(`  - Calculated KM: ${kmInPeriod.toFixed(1)}`);
          console.log(`  - Total activities: ${allCurrentActivities.length}`);
          
          // Debug: vis aktiviteter i perioden
          const activitiesInPeriod = allCurrentActivities.filter(a => {
            const activityDate = new Date(a.date);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return a.type === 'Run' && activityDate >= start && activityDate <= end;
          });
          console.log(`  - Activities in period: ${activitiesInPeriod.length}`);
          activitiesInPeriod.forEach(a => {
            console.log(`    * ${a.name}: ${a.km.toFixed(1)} km on ${a.date}`);
          });
        } else {
          // Ingen startDate eller createdAt
          console.warn(`Competition ${compId} has no startDate or createdAt, skipping`);
        }
      }

      // Oppdater leaderboard med beregnet total km (ikke additivt)
      await updateLeaderboard(user.uid, {
        name: String(profile.name || user.displayName || "Løper"),
        avatar: String(profile.avatar || "🏃‍♂️"),
        totalKm: calculatedTotalKm,
        competitionKm: competitionStats,
        lastSync: new Date().toISOString()
      });

      if (addedCount === 0) {
        const message = `Ingen nye aktiviteter å synkronisere\n${skippedCount} aktiviteter var allerede synkronisert${invalidCount > 0 ? `\n${invalidCount} aktiviteter ble hoppet over (ugyldig data)` : ''}\n\nKonkurranse-statistikk oppdatert!`;
        alert(message);
      } else {
        const message = `Synkronisert ${addedCount} nye aktiviteter (${totalNewKm.toFixed(1)} km)${skippedCount > 0 ? `\n${skippedCount} aktiviteter var allerede synkronisert` : ''}${invalidCount > 0 ? `\n${invalidCount} aktiviteter ble hoppet over (ugyldig data)` : ''}\n\nTotal km: ${calculatedTotalKm.toFixed(1)} km`;
        alert(message);
      }

    } catch (error) {
      console.error('Sync error:', error);
      alert('Kunne ikke synkronisere: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  return { syncing, syncActivities };
};
